import { get, isObject, set } from 'lodash'

import type { BillingDraftChange, SkippedDraftField } from '../types'

/** Reasons the mappers attach to skipped fields. The UI translates them via `features.BillingComposer.skip_<reason>`. */
export type SkipReason =
  | 'immutableOnEdit'
  | 'unknownField'
  | 'unsupportedValue'
  | 'pricingModelImmutable'
  | 'notInForm'

export function skipped(path: string, reason: SkipReason): SkippedDraftField {
  return { path, reason }
}

/**
 * Narrows a draft's `fields` to the paths the user ticked in the change list. Paths are dotted
 * (`flatRatePricing.unitAmount`); a top-level path keeps the whole sub-object. Paths that do not exist
 * in `fields` are ignored, so a stale change entry cannot inject `undefined` into the form.
 *
 * Leaf paths that walk into an array (or the `metadata` map) are promoted to that collection.
 * The form mappers assign collections wholesale, so copying only
 * `timeOfUsePricing.rates.0.idleFeeConfig.gracePeriod` would apply a one-element stub rate and
 * drop the rest of the schedule.
 */
export function pickDraftFields<T extends Record<string, unknown>>(fields: T, paths: readonly string[]): Partial<T> {
  const picked: Record<string, unknown> = {}
  for (const path of paths) {
    const collectionPath = collectionPathFor(fields, path)
    const value = get(fields, collectionPath)
    if (value === undefined) continue
    set(picked, collectionPath, value)
  }
  return picked as Partial<T>
}

/** Closest ancestor the mappers replace as a unit, or `path` when the leaf can be copied as-is. */
function collectionPathFor(fields: Record<string, unknown>, path: string): string {
  const segments = path.split('.')
  if (segments[0] === 'metadata') return 'metadata'

  let current: unknown = fields
  for (const [i, segment] of segments.entries()) {
    if (!isRecord(current) && !Array.isArray(current)) return path
    const next = Array.isArray(current) ? current[Number(segment)] : current[segment]
    if (Array.isArray(next) && i < segments.length - 1) {
      return segments.slice(0, i + 1).join('.')
    }
    current = next
  }
  return path
}

/** Paths of the changes the user has selected; `undefined` when the draft carries no change list (create mode). */
export function selectedChangePaths(
  changes: BillingDraftChange[],
  selectedPaths: ReadonlySet<string> | undefined
): string[] | undefined {
  if (changes.length === 0) return undefined
  return changes.map((change) => change.path).filter((path) => (selectedPaths ? selectedPaths.has(path) : true))
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return isObject(value) && !Array.isArray(value)
}

export function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
}

export function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

export function isOneOf<const T extends readonly string[]>(value: unknown, allowed: T): value is T[number] {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
}
